import { MODELS, type Model } from '../data/models'
import { resolveModelIdAliases } from '../data/brands'

/**
 * Human-readable имя модели по бэк-идентификатору. Тонкая обёртка над
 * `getModelVisuals` — возвращает только name, без иконки. Удобно когда
 * нужно отобразить «GPT 5.4» вместо технического «gpt-5.4».
 *
 * Defense in depth: если backendModel не найден в каталоге, возвращается
 * сам ID — пользователь видит технический ID, не пустую строку.
 */
export function formatModelName(
	backendModel: string | null | undefined,
	modelList?: Model[],
): string {
	if (!backendModel) return ''
	return getModelVisuals(backendModel, modelList).name
}

export function getModelVisuals(
	backendModel: string | null,
	modelList?: Model[],
): { icon: string; name: string } {
	if (!backendModel) return { icon: '', name: '' }

	const list = modelList && modelList.length > 0 ? modelList : MODELS

	// Резолюция через канонические алиасы (brands.ts): short canonical
	// `kling-v3-motion` → vendor long `kwaivgi/kling-v3-motion-control`.
	// Без этого historic Request.model в short-форме не находил бы модель
	// в каталоге, который пришёл с бэка в vendor-форме.
	for (const alias of resolveModelIdAliases(backendModel)) {
		const exact = list.find(
			(model) => model.backendModel === alias || model.id === alias,
		)
		if (exact) return { icon: exact.icon, name: exact.name }
	}

	// Suffix match — бэк хранит модель без provider-префикса
	// (`nano-banana-pro`), фронтовый каталог — с префиксом
	// (`google/nano-banana-pro`). Не всегда нужно, но display-helper'у
	// важно показать human-readable name везде где модель действительно
	// есть в каталоге под другим ID.
	const suffix = `/${backendModel}`
	const fuzzy = list.find(
		(model) => model.id.endsWith(suffix) || model.backendModel.endsWith(suffix),
	)
	if (fuzzy) return { icon: fuzzy.icon, name: fuzzy.name }

	if (
		backendModel.startsWith('gpt') ||
		backendModel.startsWith('o3') ||
		backendModel.startsWith('o4')
	) {
		return { icon: '/icons/gpt.svg', name: backendModel }
	}

	if (backendModel.startsWith('claude')) {
		return { icon: '/icons/claude.svg', name: backendModel }
	}

	if (backendModel.startsWith('grok')) {
		return { icon: '/icons/grok.svg', name: backendModel }
	}

	if (backendModel.startsWith('gemini')) {
		return { icon: '/icons/gemini.svg', name: backendModel }
	}

	if (backendModel.startsWith('deepseek')) {
		return { icon: '/icons/deepseek.svg', name: backendModel }
	}

	return { icon: '', name: backendModel }
}
